// Severity story (8.0.0 / UX Q6) — resolve the EFFECTIVE class model the
// way the DefectDrawer (the reference implementation) does:
//
//   1. The DA-level override (`detail.activeClassModelId`), resolved against
//      the global `GET /class-models` list (SB6-ANA-DA-03).
//   2. Fallback: the active catalogue's embedded `classModel`
//      (cataloguesStore — populated by CataloguesBootstrap).
//   3. `null` when neither is available (legacy/direct test mounts) — the
//      consumer then falls back to the contract-enum vocabulary.
//
// The "None" model is detected by `classCount === 0`, NEVER by a hard-coded
// UUID (project memory `reference_none_class_model_uuid` — phase4 mints a
// fresh UUID on every remigration).
import { useMemo } from "react";
import type { DefectClass } from "@aeriosiq/types";
import { useCataloguesStore } from "./cataloguesStore.js";
import { useClassModelsQuery } from "./useClassModelsQuery.js";

export interface EffectiveClassModel {
  classModelId: string;
  classCount: number;
  classes: readonly DefectClass[];
}

export interface UseEffectiveClassModelResult {
  /** Resolved model, or null when no class-model context is available. */
  classModel: EffectiveClassModel | null;
  /** `class_count === 0` — the explicit "None" (ungraded) model. */
  isNoneModel: boolean;
  /** The model's grades sorted by `sortOrder`; `[]` for None/unresolved. */
  grades: readonly DefectClass[];
}

export function useEffectiveClassModel(
  activeClassModelId?: string | null,
): UseEffectiveClassModelResult {
  const classModelsQuery = useClassModelsQuery();
  const availableCatalogues = useCataloguesStore((s) => s.availableCatalogues);
  const activeCatalogueId = useCataloguesStore((s) => s.activeCatalogueId);

  return useMemo<UseEffectiveClassModelResult>(() => {
    let classModel: EffectiveClassModel | null = null;
    if (activeClassModelId) {
      const found = classModelsQuery.data?.items.find(
        (m) => m.classModelId === activeClassModelId,
      );
      if (found) classModel = found;
    }
    if (!classModel) {
      const activeCatalogue = activeCatalogueId
        ? availableCatalogues.find((c) => c.catalogueId === activeCatalogueId)
        : undefined;
      classModel = activeCatalogue?.classModel ?? null;
    }
    const isNoneModel = classModel?.classCount === 0;
    const grades = isNoneModel
      ? []
      : [...(classModel?.classes ?? [])].sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );
    return { classModel, isNoneModel, grades };
  }, [
    activeClassModelId,
    classModelsQuery.data,
    availableCatalogues,
    activeCatalogueId,
  ]);
}
